import { FieldTypes } from "pages/employer-of-record/components/AutoForm";
import { countryIs, field, REQUIRED } from "./utils";
import {
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
} from "pages/employer-of-record/onboarding/profile-info/form/cy/constants.cy";

export const cyprusFields = {
  cyGender: field({
    type: FieldTypes.select,
    informationType: "identification",
    name: "cyGender",
    label: "Gender",
    placeholder: "Select Gender",
    options: GENDER_OPTIONS,
    validations: REQUIRED,
    scenarios: {
      onboarding: countryIs("CY"),
    },
    profileSection: "personalInfo",
    value: () => "",
  }),

  cyMaritalStatus: field({
    type: FieldTypes.select,
    informationType: "identification",
    name: "cyMaritalStatus",
    label: "Marital Status",
    placeholder: "Select Marital Status",
    options: MARITAL_STATUS_OPTIONS,
    validations: REQUIRED,
    scenarios: {
      onboarding: countryIs("CY"),
    },
    profileSection: "personalInfo",
    value: () => "",
  }),

  cySocialInsuranceNumber: field({
    type: FieldTypes.sensitiveText,
    informationType: "identification",
    name: "cySocialInsuranceNumber",
    label: "Social Insurance Number",
    placeholder: "Enter Social Insurance Number",
    message: "Must be 7 or 8 digits",
    validations: {
      ...REQUIRED,
      text: {
        matches: {
          value: /^\d{7,8}$/,
          message: "Must be 7 or 8 digits",
        },
      },
    },
    scenarios: {
      onboarding: countryIs("CY"),
    },
    profileSection: "personalInfo",
    value: () => "",
  }),

  cyTaxIdTic: field({
    type: FieldTypes.sensitiveText,
    informationType: "taxId",
    name: "cyTaxIdTic",
    label: "Tax Identification Number (TIC)",
    placeholder: "Enter Tax ID",
    message: "8 digits followed by a letter",
    validations: {
      ...REQUIRED,
      text: {
        matches: {
          value: /^\d{8}[A-Z]$/,
          message: "8 digits followed by a letter",
        },
      },
    },
    scenarios: {
      onboarding: countryIs("CY"),
    },
    profileSection: "personalInfo",
    value: () => "",
  }),

  cyConfirmTaxIdTic: field({
    type: FieldTypes.sensitiveText,
    informationType: "taxId",
    name: "confirmCyTaxIdTic",
    label: "Confirm Tax Identification Number (TIC)",
    placeholder: "Confirm Tax ID",
    validations: {
      ...REQUIRED,
      text: {
        fn: {
          value: (value, context) => value === context.parent.cyTaxIdTic,
          message: "TIC does not match. Please try again.",
        },
      },
    },
    scenarios: {
      onboarding: countryIs("CY"),
    },
    profileSection: "personalInfo",
    value: () => "",
    serialize: () => undefined,
  }),

  cyNationalId: field({
    type: FieldTypes.sensitiveText,
    informationType: "identification",
    name: "cyNationalId",
    label: "Cyprus National ID",
    placeholder: "Enter National ID",
    validations: {
      required: {
        when: {
          nationality: (value: unknown): boolean => (value as string) === "CY",
        },
      },
    },
    hideWhen: {
      nationality: (value: unknown): boolean => (value as string) !== "CY",
    },
    scenarios: {
      onboarding: countryIs("CY"),
    },
    profileSection: "personalInfo",
    value: () => "",
  }),

  cyArcNumber: field({
    type: FieldTypes.sensitiveText,
    informationType: "identification",
    name: "cyArcNumber",
    label: "ARC Number",
    placeholder: "Enter ARC Number (if applicable)",
    message: "For non-EU nationals (if applicable)",
    validations: {
      required: {
        enabled: false,
        message: "",
      },
      text: {
        matches: {
          value: /^\d*$/,
          message: "Digits only",
        },
      },
    },
    scenarios: {
      onboarding: countryIs("CY"),
    },
    profileSection: "personalInfo",
    value: () => "",
  }),

  cyLocalNationalIdOrPassport: field({
    type: FieldTypes.sensitiveText,
    informationType: "identification",
    name: "cyLocalNationalIdOrPassport",
    label: "Local National ID or Passport Number",
    placeholder: "Enter ID or Passport Number",
    validations: {
      required: {
        when: {
          nationality: (value: unknown): boolean =>
            !!((value as string) && (value as string) !== "CY"),
        },
      },
    },
    hideWhen: {
      nationality: (value: unknown): boolean => (value as string) === "CY",
    },
    scenarios: {
      onboarding: countryIs("CY"),
    },
    profileSection: "personalInfo",
    value: () => "",
  }),

  cyDateOfArrival: field({
    type: FieldTypes.date,
    informationType: "identification",
    name: "cyDateOfArrival",
    label: "Date of Arrival in Cyprus",
    placeholder: "Select date",
    validations: {
      required: {
        when: {
          nationality: (value: unknown): boolean =>
            !!((value as string) && (value as string) !== "CY"),
        },
      },
    },
    hideWhen: {
      nationality: (value: unknown): boolean => (value as string) === "CY",
    },
    scenarios: {
      onboarding: countryIs("CY"),
    },
    profileSection: "personalInfo",
    value: () => "",
  }),

  cyStartDateFirstJob: field({
    type: FieldTypes.date,
    informationType: "identification",
    name: "cyStartDateFirstJob",
    label: "Start Date of First Job",
    placeholder: "Select date",
    validations: {
      required: {
        when: {
          nationality: (value: unknown): boolean =>
            !!((value as string) && (value as string) !== "CY"),
        },
      },
    },
    hideWhen: {
      nationality: (value: unknown): boolean => (value as string) === "CY",
    },
    scenarios: {
      onboarding: countryIs("CY"),
    },
    profileSection: "personalInfo",
    value: () => "",
  }),
};

export default cyprusFields;
